import { useState, useEffect, useCallback } from "react";
import { getAvailabilitySummary } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Phone } from "lucide-react";
import { toast } from "sonner";
import { AvailabilitySummaryBar, AvailabilityEventCard } from "@/components/availability/AvailabilityComponents";

export function CrewAvailabilityWidget() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getAvailabilitySummary();
      setData(res.data);
    } catch (err) { toast.error("Failed to load availability data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => {
    const interval = setInterval(fetchSummary, 30000);
    return () => clearInterval(interval);
  }, [fetchSummary]);

  const totalPending = data.reduce((s, e) => s + e.pending, 0);
  const totalConfirmed = data.reduce((s, e) => s + e.confirmed, 0);
  const totalDeclined = data.reduce((s, e) => s + e.declined, 0);
  const totalSent = data.reduce((s, e) => s + e.checks_sent, 0);

  if (loading || (data.length === 0 && totalSent === 0)) return null;

  return (
    <Card data-testid="crew-availability-widget" className="border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Phone className="w-4 h-4" /> Crew Availability
          </CardTitle>
          <Button data-testid="avail-widget-refresh" variant="ghost" size="icon" className="h-7 w-7" onClick={fetchSummary}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent data-testid="crew-availability-content">
        <AvailabilitySummaryBar totalSent={totalSent} totalConfirmed={totalConfirmed} totalDeclined={totalDeclined} totalPending={totalPending} />
        <div className="space-y-2">
          {data.map(ev => (
            <AvailabilityEventCard
              key={ev.event_id}
              ev={ev}
              isExpanded={expanded === ev.event_id}
              onToggle={() => setExpanded(expanded === ev.event_id ? null : ev.event_id)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
